import {
	CloseSubMenuButton,
	MainNavigationIcon,
	MainNavigationIconWrapper,
	MainNavigationLinks,
	NavigationContainer,
	NavigationItemButton,
	NavigationPlaceNewOrderButton,
	SubNavigationButton,
	SubNavigationLinks
} from "./appNavigation.styles";
import LeavePageModal from "../LeavePageModal/LeavePageModal";
import { Dispatch, SetStateAction, useContext } from "react";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import { models } from "types/api/viewModels";
import ConfigurationsContext from "context/configurations/ConfigurationsContext.ts";
import {
	AreYouSureText,
	AreYouSureTruckText,
	InformationWillBeWipedText,
	ItemsWillClearText,
	NoContinueOrdering,
	NoContinueText
} from "constants/text";
import { useUserLocation } from "hooks/useUserLocation.ts";
import { UserChannel, UserRole } from "types/declarations/enums.ts";

interface Props {
	currentPath: string;
	handleCloseSubMenu: () => void;
	handleClickNavigationOption: (navItem: models["UserNavigationItemViewModel"], index: number) => void;
	leavePageModalOpen: boolean;
	mainNavigationItems: Array<models["UserNavigationItemViewModel"]>;
	selectedIndex: number | undefined;
	setLeavePageModalOpen: Dispatch<SetStateAction<boolean>>;
	subMenuOpen: boolean;
	subNavigation: models["UserNavigationItemViewModel"] | null;
	selectedNavItemLink?: string | null;
	userDetails?: models["UserViewModel"];
}

const AppNavigation = ({
	currentPath,
	handleClickNavigationOption,
	handleCloseSubMenu,
	leavePageModalOpen,
	mainNavigationItems,
	selectedIndex,
	setLeavePageModalOpen,
	subMenuOpen,
	subNavigation,
	selectedNavItemLink,
	userDetails
}: Props) => {
	const { configurationsData } = useContext(ConfigurationsContext);
	const { isTrucksPage } = useUserLocation();
	const placeOrderId = "nav-orders-place-order";
	const truckOrdersId = "nav-orders-truck-orders";
	const customQuoteId = "nav-custom-quotes";
	const marketingMaterialsId = "nav-marketing-materials";

	const isRetail =
		userDetails?.userChannel === UserChannel.LOWES ||
		userDetails?.userChannel === UserChannel.HOME_DEPOT ||
		userDetails?.userChannel === UserChannel.MENARDS;

	return (
		<NavigationContainer subMenuOpen={subMenuOpen}>
			<LeavePageModal
				open={leavePageModalOpen}
				url={selectedNavItemLink ?? currentPath}
				continueOrdering={() => setLeavePageModalOpen(false)}
				closeModal={() => setLeavePageModalOpen(false)}
				headerText={isTrucksPage ? AreYouSureTruckText : AreYouSureText}
				subHeaderText={isTrucksPage ? InformationWillBeWipedText : ItemsWillClearText}
				continueButtonText={isTrucksPage ? NoContinueText : NoContinueOrdering}
			/>
			<MainNavigationLinks data-testid="main-navigation-link-container">
				{mainNavigationItems.map((mainNavigationItem, index) => (
					<NavigationItemButton
						data-id={mainNavigationItem.id}
						data-testid={mainNavigationItem.id}
						key={mainNavigationItem.title}
						selected={index === selectedIndex}
						onClick={() => handleClickNavigationOption(mainNavigationItem, index)}
					>
						<MainNavigationIconWrapper>
							<MainNavigationIcon
								data-testid={`${mainNavigationItem.id}-logo`}
								src={"/assets/navigation_logos/" + mainNavigationItem.icon}
							/>
						</MainNavigationIconWrapper>
						<div>
							{mainNavigationItem.title}
							{index === selectedIndex && (
								<img
									alt={mainNavigationItem.title ?? ""}
									src="/assets/navigation_logos/SelectedNavItemBlueDot.svg"
								/>
							)}
						</div>
					</NavigationItemButton>
				))}
			</MainNavigationLinks>
			<SubNavigationLinks data-testid="sub-navigation-link-container">
				<CloseSubMenuButton
					data-testid="navigation-close-sub-menu-button"
					onClick={handleCloseSubMenu}
				>
					<ChevronLeft />
					{subNavigation?.title}
				</CloseSubMenuButton>
				{subNavigation?.navigationItems?.map((subNavigationItem, index) => {
					if (
						subNavigationItem?.id === placeOrderId &&
						configurationsData?.featureFlags?.createOrdersEnabled &&
						(userDetails?.role !== UserRole.INTERNAL_DESIGNER ||
							configurationsData.featureFlags.proQuoteEnabled)
					) {
						return (
							<NavigationPlaceNewOrderButton
								key={subNavigationItem.title}
								variant="contained"
								data-id="navigation-place-new-order-button"
								data-testid="navigation-place-new-order-button"
								fullWidth
								onClick={() => handleClickNavigationOption(subNavigationItem, index)}
							>
								{subNavigationItem.title}
							</NavigationPlaceNewOrderButton>
						);
					} else if (
						subNavigationItem.id !== placeOrderId &&
						!(subNavigationItem.id === truckOrdersId && !configurationsData?.featureFlags?.trucksEnabled) &&
						!(
							subNavigationItem.id === customQuoteId &&
							!configurationsData?.featureFlags?.customQuoteEnabled
						) &&
						!(subNavigationItem.id === marketingMaterialsId && isRetail)
					) {
						return (
							<SubNavigationButton
								data-id={subNavigationItem.id}
								data-testid={`navigation-subMenu-${subNavigationItem.id}`}
								key={subNavigationItem.title}
								onClick={() => handleClickNavigationOption(subNavigationItem, index)}
							>
								{subNavigationItem.title}
							</SubNavigationButton>
						);
					}
				})}
			</SubNavigationLinks>
		</NavigationContainer>
	);
};

export default AppNavigation;
